import { ReactNode } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/utils/classes";

/* Variants */
const variants = cva("card relative bg-base-100 border-2 border-base-300 rounded-2xl h-full cursor-pointer hover:bg-base-200 transition-all duration-300 ease-out", {
  variants: {
    selected: {
      true: "card-active border-primary bg-primary hover:bg-primary",
      false: "",
    },
    size: {
      small: "w-auto max-w-60 rounded-lg",
      large: "min-w-72 max-w-60",
      auto: "w-auto",
    },
  },
  defaultVariants: {
    size: "large",
  },
});

/* Prop Types */
interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof variants> {
  imageUrl?: string;
  selected?: boolean;
  winner?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  imgFit?: "cover" | "contain";
}

/*
 * Card Component
 */
export const Card = ({ imageUrl, selected, size, imgFit = "cover", children, onClick, winner, className }: CardProps) => {
  /* Render */
  return (
    <div
      className={cn("relative border-none rounded-2xl", {
        "bg-linear-to-r from-[#4E499C] to-[#F2318A] p-0.5": winner,
      })}
    >
      <div className={cn(variants({ selected, size }), className)} onClick={onClick}>
        {/* Card Container */}
        <div className={cn("h-full rounded-2xl pb-1", { "rounded-lg pb-0 overflow-hidden": size === "small" })}>
          {/* Card Image */}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="thumbnail"
              crossOrigin="use-credentials"
              className={cn("w-full bg-base-200 h-[184px] rounded-t-2xl", {
                "object-cover": imgFit === "cover",
                "object-contain": imgFit === "contain",
                "max-h-28 rounded-t-lg": size === "small",
              })}
              draggable={false}
            />
          ) : (
            // Empty Image Placeholder
            <div
              className={cn("w-full bg-base-200 h-[184px] rounded-t-2xl", {
                "object-cover": imgFit === "cover",
                "object-contain": imgFit === "contain",
                "max-h-28 rounded-t-lg": size === "small",
              })}
            ></div>
          )}

          {/* Card Content */}
          {children}
        </div>
      </div>

      {/* Winner Badge */}
      {winner && <div className="absolute top-4 right-4 bg-pink-500 text-white shadow-md px-2 py-1 rounded-lg text-sm font-medium">Top score</div>}
    </div>
  );
};
